import { useState, useEffect, useRef, type ElementType, type ChangeEvent, type ReactNode } from 'react';
import { ArrowLeft, Building2, Users, Settings, BarChart3, AlertTriangle, RefreshCw, X, FolderOpen, Lock, ScrollText, ShieldAlert, Archive } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { uploadFirmLogo, removeFirmLogo, FIRM_LOGO_ACCEPTED_TYPES } from '../lib/firmLogo';
import { countOccupiedSeats, countAvailableSeats } from '../lib/firmSeats';
import { SEAT_MIN, SEAT_MAX, clampSeats } from '../lib/enterprisePricing';
import {
  RETENTION_MIN_DAYS,
  RETENTION_MAX_DAYS,
  validateRetentionPolicy,
  formatRetentionWindow,
  parseRetentionReport,
  formatRetentionAction,
  type RetentionDays,
  type RetentionReport,
} from '../lib/retentionPolicy';
import {
  formatAuditAction,
  resolvePerson,
  resolveAuditTarget,
  summarizeAuditDetail,
  formatAuditTimestamp,
  type FirmAuditRow,
} from '../lib/firmAuditLog';

type FirmTab = 'overview' | 'members' | 'projects' | 'activity' | 'settings';

interface FirmAdminDashboardProps {
  onClose: () => void;
}

interface Firm {
  id: string;
  name: string;
  /** Public URL of the firm's logo, or null. Written only from Settings. */
  logo_url: string | null;
  seat_count: number;
  subscription_status: string;
  project_visibility: string;
  project_edit_permission: string;
  mfa_policy: 'off' | 'all';
  mfa_backup_codes_allowed: boolean;
  mfa_reprompt_days: number | null;
  security_contact_name: string | null;
  security_contact_email: string | null;
  /** Days after completion before raw AMI intervals are deleted. Null = off. */
  retention_intervals_days: number | null;
  /** Days after completion before end-customer identity is cleared. Null = off. */
  retention_identity_days: number | null;
}

interface Member {
  id: string;
  full_name: string | null;
  email: string;
  firm_admin: boolean;
  has_seat: boolean;
  last_active_at: string | null;
  created_at: string;
}

// Row shape of the get_firm_project_directory() RPC — metadata only, never
// project contents. The dashboard must not link into a project from here.
interface DirectoryProject {
  project_id: string;
  project_name: string | null;
  owner_user_id: string | null;
  owner_display_name: string | null;
  created_at: string | null;
  updated_at: string | null;
  status: string | null;
  results_unlocked: boolean;
}

interface PendingInvite {
  id: string;
  invited_email: string;
  created_at: string;
  expires_at: string;
}

const NAV_ITEMS: { id: FirmTab; label: string; Icon: ElementType }[] = [
  { id: 'overview', label: 'Overview', Icon: BarChart3 },
  { id: 'members', label: 'Members', Icon: Users },
  { id: 'projects', label: 'Projects', Icon: FolderOpen },
  { id: 'activity', label: 'Activity', Icon: ScrollText },
  { id: 'settings', label: 'Settings', Icon: Settings },
];

async function getToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? '';
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    past_due: 'bg-yellow-100 text-yellow-800',
    canceled: 'bg-red-100 text-red-800',
    inactive: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] ?? 'bg-gray-100 text-gray-700'}`}>
      {status === 'past_due' ? 'Past Due' : status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function CapabilityBadges({ member }: { member: Member }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {member.firm_admin && (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Admin</span>
      )}
      {member.has_seat && (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Seat</span>
      )}
    </span>
  );
}

function ConfirmDialog({ title, message, onConfirm, onCancel, confirmLabel = 'Remove' }: {
  title: string;
  /** ReactNode, not string: a confirmation that has to spell out what is lost
      needs more than one sentence. */
  message: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-gray-900">{title}</h3>
            <div className="text-sm text-gray-600 mt-1 space-y-2">{message}</div>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">Cancel</button>
          <button onClick={onConfirm} data-testid="confirm-dialog-confirm" className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-md">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────
// Seats and members are different numbers and are shown as such: a firm admin
// who runs no projects is a member holding no seat, and is free.
function OverviewTab({ firm, members, pendingCount }: { firm: Firm; members: Member[]; pendingCount: number }) {
  const memberCount = members.length;
  const seatsUsed = countOccupiedSeats(members, pendingCount);
  const seated = members.filter(m => m.has_seat).length;
  const available = countAvailableSeats(firm.seat_count, members, pendingCount);
  const freeAdmins = memberCount - seated;
  const isInactive = firm.subscription_status !== 'active';

  return (
    <div className="space-y-6">
      {isInactive && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">Your subscription is not active. Members cannot access TrueLoad until billing is resolved.</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Seats Used</p>
          <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="firm-seats-used">
            {seatsUsed} / {firm.seat_count}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {memberCount} {memberCount === 1 ? 'member' : 'members'}
            {freeAdmins > 0 && <>, {freeAdmins} admin-only (free)</>}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Pending Invitations</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{pendingCount}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Subscription</p>
          <div className="mt-2"><StatusBadge status={firm.subscription_status} /></div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Plan</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">Enterprise</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        Your firm has <strong>{firm.seat_count}</strong> paid seats. <strong>{seated}</strong> {seated === 1 ? 'is' : 'are'} in use
        {pendingCount > 0 && <>, <strong>{pendingCount}</strong> promised to pending invitations</>}
        {available > 0 && <>, and <strong>{available}</strong> {available === 1 ? 'is' : 'are'} free to assign</>}.
        {freeAdmins > 0 && (
          <> Admins who don't run projects don't use a seat, so <strong>{freeAdmins}</strong> of your
          members {freeAdmins === 1 ? 'costs' : 'cost'} nothing.</>
        )}
      </div>
    </div>
  );
}

// ── Members Tab ──────────────────────────────────────────────────────────────
function MembersTab({ firm, members, pendingInvites, onRefresh }: {
  firm: Firm;
  members: Member[];
  pendingInvites: PendingInvite[];
  onRefresh: () => void;
}) {
  const { userProfile } = useAuth();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState<Member | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [capabilityError, setCapabilityError] = useState('');
  const [capabilityBusyId, setCapabilityBusyId] = useState<string | null>(null);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteSuccess('');
    setInviteError('');
    try {
      const token = await getToken();
      const res = await fetch('/.netlify/functions/invite-firm-member', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send invitation.');
      setInviteSuccess(`Invitation sent to ${inviteEmail}.`);
      setInviteEmail('');
      onRefresh();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invitation.');
    } finally {
      setInviteLoading(false);
    }
  };

  // Removal goes through the remove_firm_member RPC, not a direct update. The
  // direct update could never have worked — the firm-admin policy's WITH CHECK
  // requires firm_id IS NOT NULL, so nulling it failed on the new row — and the
  // failure was invisible because this handler only console.error'd. The RPC
  // also refuses to strand a firm without admins and writes the audit row.
  const handleRemoveMember = async (member: Member) => {
    setRemoveLoading(true);
    setCapabilityError('');
    try {
      const { error } = await supabase.rpc('remove_firm_member', { p_target: member.id });
      if (error) throw new Error(error.message);
      onRefresh();
    } catch (err) {
      setCapabilityError(err instanceof Error ? err.message : 'Failed to remove the member.');
    } finally {
      setRemoveLoading(false);
      // Close either way: the error surface lives under the members table, so
      // leaving the modal open would hide the message behind its overlay.
      setConfirmRemove(null);
    }
  };

  // All capability changes go through the set_firm_member_capabilities RPC —
  // it refuses stranding a firm without admins and writes the audit log.
  const handleSetCapabilities = async (member: Member, makeAdmin: boolean, makeSeat: boolean) => {
    setCapabilityError('');
    setCapabilityBusyId(member.id);
    try {
      const { error } = await supabase.rpc('set_firm_member_capabilities', {
        p_target: member.id,
        p_make_admin: makeAdmin,
        p_make_seat: makeSeat,
      });
      if (error) throw new Error(error.message);
      onRefresh();
    } catch (err) {
      setCapabilityError(err instanceof Error ? err.message : 'Failed to update member capabilities.');
    } finally {
      setCapabilityBusyId(null);
    }
  };

  const handleRevokeInvite = async (invite: PendingInvite) => {
    const { error } = await supabase
      .from('firm_invitations')
      .delete()
      .eq('id', invite.id);
    if (!error) onRefresh();
  };

  return (
    <div className="space-y-6">
      {/* Invite form */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Invite a team member</h3>
        <form onSubmit={handleInvite} className="flex gap-3">
          <input
            type="email"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            required
            placeholder="colleague@example.com"
            data-testid="invite-email-input"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            type="submit"
            disabled={inviteLoading}
            data-testid="invite-submit"
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            {inviteLoading ? 'Sending…' : 'Send Invitation'}
          </button>
        </form>
        {inviteSuccess && <p className="mt-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">{inviteSuccess}</p>}
        {inviteError && <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{inviteError}</p>}
      </div>

      {/* Members table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Members</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Email', 'Capabilities', 'Last Active', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.map(m => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{m.full_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{m.email}</td>
                  <td className="px-4 py-3"><CapabilityBadges member={m} /></td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {m.last_active_at ? new Date(m.last_active_at).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleSetCapabilities(m, !m.firm_admin, m.has_seat)}
                        disabled={capabilityBusyId === m.id}
                        data-testid={`cap-admin-${m.id}`}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:text-gray-400"
                      >
                        {m.firm_admin ? 'Remove admin' : 'Make admin'}
                      </button>
                      <button
                        onClick={() => handleSetCapabilities(m, m.firm_admin, !m.has_seat)}
                        disabled={capabilityBusyId === m.id}
                        data-testid={`cap-seat-${m.id}`}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:text-gray-400"
                      >
                        {m.has_seat ? 'Revoke seat' : 'Grant seat'}
                      </button>
                      {m.id !== userProfile?.id && !m.firm_admin && (
                        <button
                          onClick={() => setConfirmRemove(m)}
                          className="text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {capabilityError && (
          <p className="mx-4 my-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="capability-error">
            {capabilityError}
          </p>
        )}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
          {members.length} member{members.length !== 1 ? 's' : ''} · Admin manages the firm; Seat runs projects. A member can hold either or both.
        </div>
      </div>

      {/* Pending invitations */}
      {pendingInvites.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Pending Invitations</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {pendingInvites.map(inv => (
              <div key={inv.id} className="px-4 py-3 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">{inv.invited_email}</p>
                  <p className="text-xs text-gray-500">
                    Sent {new Date(inv.created_at).toLocaleDateString()} · Expires {new Date(inv.expires_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => handleRevokeInvite(inv)}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 font-medium"
                >
                  <X className="w-3 h-3" /> Revoke
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {confirmRemove && (
        <ConfirmDialog
          title={`Remove ${confirmRemove.full_name || confirmRemove.email} from ${firm.name}?`}
          message="They will lose access to the firm immediately. Their projects will remain in the firm."
          onConfirm={() => { if (!removeLoading) handleRemoveMember(confirmRemove); }}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
}

// ── Projects Tab ──────────────────────────────────────────────────────────────
// Firm admins see a directory of project metadata via the
// get_firm_project_directory RPC — they have no row access to project
// contents, so nothing here links into a project.
function ProjectsTab({ members }: { members: Member[] }) {
  const [projects, setProjects] = useState<DirectoryProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reassignTarget, setReassignTarget] = useState<DirectoryProject | null>(null);
  const [reassignOwner, setReassignOwner] = useState('');
  const [reassignBusy, setReassignBusy] = useState(false);
  const [reassignError, setReassignError] = useState('');

  const seatedMembers = members.filter(m => m.has_seat);

  const loadDirectory = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: rpcError } = await supabase.rpc('get_firm_project_directory');
      if (rpcError) throw new Error(rpcError.message);
      setProjects((data as DirectoryProject[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the project directory.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDirectory(); }, []);

  const openReassign = (p: DirectoryProject) => {
    setReassignTarget(p);
    setReassignError('');
    const firstOther = seatedMembers.find(m => m.id !== p.owner_user_id);
    setReassignOwner(firstOther?.id ?? '');
  };

  const handleReassign = async () => {
    if (!reassignTarget || !reassignOwner) return;
    setReassignBusy(true);
    setReassignError('');
    try {
      const { error: rpcError } = await supabase.rpc('reassign_firm_project', {
        p_project_id: reassignTarget.project_id,
        p_new_owner: reassignOwner,
      });
      if (rpcError) throw new Error(rpcError.message);
      setReassignTarget(null);
      loadDirectory();
    } catch (err) {
      setReassignError(err instanceof Error ? err.message : 'Failed to reassign the project.');
    } finally {
      setReassignBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Firm Projects</h3>
          <button onClick={loadDirectory} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {loading && <div className="text-center py-10 text-gray-500 text-sm">Loading projects…</div>}
        {error && !loading && (
          <p className="m-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
        )}

        {!loading && !error && (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm" data-testid="firm-project-directory">
                <thead className="bg-gray-50">
                  <tr>
                    {['Project', 'Owner', 'Status', 'Created', 'Updated', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {projects.map(p => (
                    <tr key={p.project_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{p.project_name || 'Untitled Project'}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{p.owner_display_name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                          {p.status ? p.status.charAt(0).toUpperCase() + p.status.slice(1) : '—'}
                          {p.results_unlocked && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200">
                              <Lock className="w-3 h-3" /> Unlocked
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openReassign(p)}
                          data-testid={`btn-reassign-${p.project_id}`}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Reassign
                        </button>
                      </td>
                    </tr>
                  ))}
                  {projects.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">
                        No projects in the firm yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
              {projects.length} project{projects.length !== 1 ? 's' : ''} · Directory shows project names, owners, and status only. Project contents are visible to seated members per your access settings.
            </div>
          </>
        )}
      </div>

      {reassignTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="font-semibold text-gray-900 mb-1">
              Reassign “{reassignTarget.project_name || 'Untitled Project'}”
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Choose a member with a seat to become the project owner.
              {reassignTarget.owner_display_name && <> Current owner: {reassignTarget.owner_display_name}.</>}
            </p>
            <select
              value={reassignOwner}
              onChange={e => setReassignOwner(e.target.value)}
              data-testid="reassign-owner-select"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="" disabled>Select a seated member…</option>
              {seatedMembers.map(m => (
                <option key={m.id} value={m.id}>
                  {(m.full_name || m.email)}{m.id === reassignTarget.owner_user_id ? ' (current owner)' : ''}
                </option>
              ))}
            </select>
            {seatedMembers.length === 0 && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
                No members hold a seat. Grant a seat on the Members tab first.
              </p>
            )}
            {reassignError && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-4" data-testid="reassign-error">{reassignError}</p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setReassignTarget(null)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleReassign}
                disabled={reassignBusy || !reassignOwner}
                data-testid="reassign-confirm"
                className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-md"
              >
                {reassignBusy ? 'Reassigning…' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Activity Tab ──────────────────────────────────────────────────────────────
// The firm's audit trail. This is the first client read of firm_audit_log and
// the first time its "Firm admins can view firm audit log" SELECT policy runs
// against a user JWT rather than the service role.
//
// THE TRAP THIS TAB IS BUILT AROUND: firm_audit_log carries the restrictive MFA
// policy from 20260802145824. An unelevated session reads it as **HTTP 200,
// zero rows, no error** — indistinguishable from a firm that has done nothing
// yet, unless you look somewhere other than the response. Showing "No activity
// recorded" to an admin whose session simply is not elevated would be the audit
// trail lying about itself, which is worse than showing nothing at all. So an
// empty result is only ever reported as empty once `mfaGate === 'none'` says
// the gate resolved cleanly. This is the same failure mode that wiped local
// project caches on 2026-08-02; there it deleted data, here it would conceal
// evidence.
const AUDIT_PAGE_SIZE = 100;

// ── Data Retention report (Activity tab) ─────────────────────────────────────
// Read-only. Everything here comes from get_firm_retention_report(), which
// counts what the nightly sweep has actually done rather than what the policy
// says it should have done. It is meant to be screenshotted or read aloud
// during a customer's security review, so every figure is labelled in words
// and nothing is a chart.
//
// It inherits the Activity tab's aal1 trap for the same reason and with the
// same rule: this data sits behind the firm's restrictive MFA policy, an
// unelevated session can read it as zeroes with no error, and a retention
// report that reports zero purges to an auditor because the reader's session
// was not elevated would be worse than showing nothing. So an all-zero report
// is only ever presented as an all-zero report once `mfaGate === 'none'`.

function RetentionFigure({ label, value, hint, testId, alarm = false }: {
  label: string; value: string; hint?: string; testId: string; alarm?: boolean;
}) {
  return (
    <div className={`rounded-md border px-4 py-3 ${alarm ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
      <p className={`text-xs font-medium uppercase tracking-wide ${alarm ? 'text-red-700' : 'text-gray-500'}`}>{label}</p>
      <p className={`mt-1 text-lg font-semibold ${alarm ? 'text-red-800' : 'text-gray-900'}`} data-testid={testId}>{value}</p>
      {hint && <p className={`mt-0.5 text-xs ${alarm ? 'text-red-700' : 'text-gray-500'}`}>{hint}</p>}
    </div>
  );
}

function formatReportDate(iso: string | null): string {
  if (!iso) return 'None';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function RetentionReportPanel({ firm }: { firm: Firm }) {
  const { mfaGate } = useAuth();
  const [report, setReport] = useState<RetentionReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadReport = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: rpcError } = await supabase.rpc('get_firm_retention_report', {
        p_firm_id: firm.id,
      });
      if (rpcError) throw new Error(rpcError.message);
      setReport(parseRetentionReport(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the retention report.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReport(); }, [firm.id]);

  // Only meaningful when the read itself succeeded — see the block comment.
  const emptyIsTrustworthy = mfaGate === 'none';
  const readsAsNothing = !!report
    && !report.policyEnabled
    && report.projectsCompleted === 0
    && report.projectsIntervalsPurged === 0
    && report.projectsIdentityRedacted === 0
    && report.orphanedIdentityRecords === 0
    && report.actionsLast90Days.length === 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="firm-retention-report">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Data Retention</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            What the nightly retention sweep has actually done for this firm. Read-only.
          </p>
        </div>
        <button onClick={loadReport} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading && <div className="text-center py-10 text-gray-500 text-sm">Loading retention report…</div>}

      {error && !loading && (
        <p className="m-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
      )}

      {!loading && !error && report && readsAsNothing && !emptyIsTrustworthy && (
        <div
          data-testid="firm-retention-unverified"
          className="m-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-md px-3 py-3"
        >
          <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            Your session isn't fully verified, so the retention report can't be shown. These figures are
            <strong> not</strong> zero — finish the security check and reload before reading this out or
            screenshotting it.
          </p>
        </div>
      )}

      {!loading && !error && report && !(readsAsNothing && !emptyIsTrustworthy) && (
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <RetentionFigure
              label="Active policy — raw interval data"
              value={formatRetentionWindow(report.policyIntervalsDays)}
              hint={report.policyIntervalsDays === null
                ? 'Nothing is purged automatically.'
                : 'Days after a project is completed.'}
              testId="retention-policy-intervals"
            />
            <RetentionFigure
              label="Active policy — customer identity"
              value={formatRetentionWindow(report.policyIdentityDays)}
              hint={report.policyIdentityDays === null
                ? 'Nothing is removed automatically.'
                : 'Days after a project is completed.'}
              testId="retention-policy-identity"
            />
            <RetentionFigure
              label="Projects completed"
              value={String(report.projectsCompleted)}
              hint="The population the two timers run against."
              testId="retention-projects-completed"
            />
            <RetentionFigure
              label="Projects whose intervals were purged"
              value={String(report.projectsIntervalsPurged)}
              testId="retention-projects-purged"
            />
            <RetentionFigure
              label="Projects whose identity was removed"
              value={String(report.projectsIdentityRedacted)}
              testId="retention-projects-redacted"
            />
            <RetentionFigure
              label="Oldest completed project still holding customer identity"
              value={formatReportDate(report.oldestCompletedStillIdentified)}
              hint={report.oldestCompletedStillIdentified
                ? 'Completion date of the oldest such project.'
                : 'No completed project still holds customer identity.'}
              testId="retention-oldest-identified"
            />
          </div>

          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
              Actions taken in the last 90 days
            </p>
            {report.actionsLast90Days.length === 0 ? (
              <p className="text-sm text-gray-600" data-testid="retention-actions-none">
                None. No project reached either retention window in the last 90 days.
              </p>
            ) : (
              <ul className="space-y-1" data-testid="retention-actions">
                {report.actionsLast90Days.map(a => (
                  <li key={a.action} className="text-sm text-gray-700 flex justify-between border-b border-gray-100 py-1">
                    <span>{formatRetentionAction(a.action)}</span>
                    <span className="font-semibold text-gray-900">{a.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Always zero by construction: identity is redacted the instant a
              project link is broken, and anything the triggers cannot see is
              collected by the same nightly sweep. A number above zero is a
              defect in the deletion path, not a statistic, so it is shown as
              one. */}
          <RetentionFigure
            label="Orphaned identity records"
            value={String(report.orphanedIdentityRecords)}
            alarm={report.orphanedIdentityRecords > 0}
            hint={report.orphanedIdentityRecords > 0
              ? 'This figure must always be zero. Customer identity is outliving the project it belonged to. Contact TrueLoad support and quote this panel.'
              : 'Expected. No customer identity is outliving the project it belonged to.'}
            testId="retention-orphans"
          />

          {report.generatedAt && (
            <p className="text-xs text-gray-400">
              Generated {formatAuditTimestamp(report.generatedAt)}. Figures are counted at read time, not cached.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function ActivityTab({ firm, members }: { firm: Firm; members: Member[] }) {
  const { mfaGate } = useAuth();
  const [rows, setRows] = useState<FirmAuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [truncated, setTruncated] = useState(false);

  const loadActivity = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: readError } = await supabase
        .from('firm_audit_log')
        .select('id, action, actor_user_id, target_user_id, project_id, detail, created_at')
        .order('created_at', { ascending: false })
        .limit(AUDIT_PAGE_SIZE + 1);
      if (readError) throw new Error(readError.message);
      const all = (data as FirmAuditRow[]) || [];
      setTruncated(all.length > AUDIT_PAGE_SIZE);
      setRows(all.slice(0, AUDIT_PAGE_SIZE));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load firm activity.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadActivity(); }, []);

  // Only meaningful when the read itself succeeded — see the block comment.
  const gateUnresolved = mfaGate !== 'none';
  const emptyIsTrustworthy = !gateUnresolved;

  return (
    <div className="space-y-6">
      <RetentionReportPanel firm={firm} />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Firm Activity</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Administrative actions on this firm. Recorded by TrueLoad, never editable.
            </p>
          </div>
          <button onClick={loadActivity} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {loading && <div className="text-center py-10 text-gray-500 text-sm">Loading activity…</div>}
        {error && !loading && (
          <p className="m-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
        )}

        {!loading && !error && rows.length === 0 && !emptyIsTrustworthy && (
          <div
            data-testid="firm-activity-unverified"
            className="m-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-md px-3 py-3"
          >
            <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Your session isn't fully verified, so the activity log can't be shown. This is
              <strong> not</strong> an empty log — finish the security check and reload.
            </p>
          </div>
        )}

        {!loading && !error && rows.length === 0 && emptyIsTrustworthy && (
          <div className="text-center py-10 text-gray-500 text-sm" data-testid="firm-activity-empty">
            No activity recorded yet.
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm" data-testid="firm-activity-log">
                <thead className="bg-gray-50">
                  <tr>
                    {['When', 'Action', 'By', 'Affecting', 'Details'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(r => {
                    const detail = summarizeAuditDetail(r.action, r.detail);
                    return (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                          {formatAuditTimestamp(r.created_at)}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">{formatAuditAction(r.action)}</td>
                        <td className="px-4 py-3 text-gray-700 text-xs">{resolvePerson(r.actor_user_id, members)}</td>
                        <td className="px-4 py-3 text-gray-700 text-xs">
                          {r.target_user_id ? resolveAuditTarget(r, members) : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{detail || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
              {rows.length} event{rows.length !== 1 ? 's' : ''}
              {truncated && <> · showing the {AUDIT_PAGE_SIZE} most recent — older events are retained but not listed here</>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Security policy card (Settings tab) ──────────────────────────────────────
function SecurityPolicyCard({ firm, onFirmUpdated }: { firm: Firm; onFirmUpdated: (f: Partial<Firm>) => void }) {
  const [policy, setPolicy] = useState<Firm['mfa_policy']>(firm.mfa_policy ?? 'off');
  const [backupAllowed, setBackupAllowed] = useState(firm.mfa_backup_codes_allowed ?? true);
  const [repromptMode, setRepromptMode] = useState<'device' | 'days'>(firm.mfa_reprompt_days ? 'days' : 'device');
  const [repromptDays, setRepromptDays] = useState(firm.mfa_reprompt_days ?? 30);
  const [contactName, setContactName] = useState(firm.security_contact_name ?? '');
  const [contactEmail, setContactEmail] = useState(firm.security_contact_email ?? '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setMsg('');
    setError('');
    const reprompt = repromptMode === 'days' ? repromptDays : null;
    try {
      const { error: rpcError } = await supabase.rpc('set_firm_mfa_policy', {
        p_policy: policy,
        p_backup_codes_allowed: backupAllowed,
        p_reprompt_days: reprompt,
        p_contact_name: contactName.trim() || null,
        p_contact_email: contactEmail.trim() || null,
      });
      if (rpcError) throw new Error(rpcError.message);
      onFirmUpdated({
        mfa_policy: policy,
        mfa_backup_codes_allowed: backupAllowed,
        mfa_reprompt_days: reprompt,
        security_contact_name: contactName.trim() || null,
        security_contact_email: contactEmail.trim() || null,
      });
      setMsg('Security settings saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save security settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-1">Security</h3>
      <p className="text-sm text-gray-500 mb-4">
        Two-factor authentication policy for your firm. Firm admins always require two-factor authentication regardless of this setting. All changes are recorded in the firm audit log.
      </p>

      <p className="text-sm font-medium text-gray-700 mb-2">Require two-factor authentication</p>
      <div className="space-y-2 mb-4">
        {([
          // Two options because there are only two behaviours. An "Admins only"
          // tier used to sit between these, but firm admins are always required
          // regardless of policy, so it was identical to Off — an admin could
          // pick it believing they had tightened something. Retired in
          // 20260803134946.
          { value: 'off' as const, label: 'Off', description: 'Members choose for themselves — firm admins are always required.' },
          { value: 'all' as const, label: 'On', description: 'All members required.' },
        ]).map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setPolicy(opt.value)}
            data-testid={`firm-mfa-policy-${opt.value}`}
            className={`w-full text-left rounded-lg border-2 px-4 py-2.5 transition-all ${
              policy === opt.value
                ? 'ring-2 ring-blue-600 bg-blue-50 border-blue-600'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
            <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>
          </button>
        ))}
      </div>

      <label className="flex items-center gap-2 mb-4 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={backupAllowed}
          onChange={e => setBackupAllowed(e.target.checked)}
          data-testid="firm-mfa-backup-allowed"
          className="rounded border-gray-300"
        />
        Allow members to use backup codes (off = admin-reset only)
      </label>

      <p className="text-sm font-medium text-gray-700 mb-1">Re-prompt for a code</p>
      <div className="flex items-center gap-3 mb-4">
        <select
          value={repromptMode}
          onChange={e => setRepromptMode(e.target.value as 'device' | 'days')}
          data-testid="firm-mfa-reprompt-mode"
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
        >
          <option value="device">Once per device/session</option>
          <option value="days">Every N days</option>
        </select>
        {repromptMode === 'days' && (
          <input
            type="number"
            min={1}
            max={365}
            value={repromptDays}
            onChange={e => setRepromptDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
            data-testid="firm-mfa-reprompt-days"
            className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        )}
      </div>

      <p className="text-sm font-medium text-gray-700 mb-1">Security contact</p>
      <p className="text-xs text-gray-500 mb-2">TrueLoad verifies with this person before any two-factor reset for your firm.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <input
          type="text"
          value={contactName}
          onChange={e => setContactName(e.target.value)}
          placeholder="Name"
          data-testid="firm-security-contact-name"
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
        <input
          type="email"
          value={contactEmail}
          onChange={e => setContactEmail(e.target.value)}
          placeholder="email@example.com"
          data-testid="firm-security-contact-email"
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          data-testid="firm-mfa-save"
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
        >
          {saving ? 'Saving…' : 'Save Security Settings'}
        </button>
        {msg && <p className="text-sm text-green-700">{msg}</p>}
        {error && <p className="text-sm text-red-700" data-testid="firm-mfa-error">{error}</p>}
      </div>
    </div>
  );
}

// ── Data retention card (Settings tab) ───────────────────────────────────────
// Two timers, both Off by default, both writing through
// set_firm_retention_policy(). That function is SECURITY DEFINER and writes the
// firm audit row itself, so this card must not write an audit row of its own —
// and must not try a direct UPDATE on firms either: the two retention columns
// are SELECT-granted to authenticated and nothing more, by design.
//
// The rules below (off, or 7–3650 days, identity never shorter than intervals)
// are the same rules firms_retention_window_check holds in the database. They
// are repeated here so an admin gets a sentence instead of a constraint name.

type RetentionMode = 'off' | 'days';

const RETENTION_DEFAULT_DAYS = 365;

function RetentionPolicyCard({ firm, onFirmUpdated }: { firm: Firm; onFirmUpdated: (f: Partial<Firm>) => void }) {
  const [intervalsMode, setIntervalsMode] = useState<RetentionMode>(
    firm.retention_intervals_days === null ? 'off' : 'days'
  );
  const [intervalsDays, setIntervalsDays] = useState(firm.retention_intervals_days ?? RETENTION_DEFAULT_DAYS);
  const [identityMode, setIdentityMode] = useState<RetentionMode>(
    firm.retention_identity_days === null ? 'off' : 'days'
  );
  const [identityDays, setIdentityDays] = useState(firm.retention_identity_days ?? RETENTION_DEFAULT_DAYS);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const nextIntervals: RetentionDays = intervalsMode === 'off' ? null : intervalsDays;
  const nextIdentity: RetentionDays = identityMode === 'off' ? null : identityDays;
  const validationError = validateRetentionPolicy(nextIntervals, nextIdentity);

  const save = async () => {
    setConfirming(false);
    setSaving(true);
    setMsg('');
    setError('');
    try {
      // The definer function writes the firm_audit_log row. Nothing is written
      // from here, and firms is never updated directly.
      const { error: rpcError } = await supabase.rpc('set_firm_retention_policy', {
        p_firm_id: firm.id,
        p_intervals_days: nextIntervals,
        p_identity_days: nextIdentity,
      });
      if (rpcError) throw new Error(rpcError.message);
      onFirmUpdated({
        retention_intervals_days: nextIntervals,
        retention_identity_days: nextIdentity,
      });
      setMsg(
        nextIntervals === null && nextIdentity === null
          ? 'Retention is off. Nothing will be purged automatically.'
          : 'Retention policy saved. The nightly sweep applies it from tonight.'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the retention policy.');
    } finally {
      setSaving(false);
    }
  };

  const attemptSave = () => {
    setMsg('');
    setError('');
    if (validationError) {
      setError(validationError);
      return;
    }
    setConfirming(true);
  };

  const timer = (
    which: 'intervals' | 'identity',
    label: string,
    description: string,
    mode: RetentionMode,
    setMode: (m: RetentionMode) => void,
    days: number,
    setDays: (d: number) => void
  ) => (
    <div className="rounded-lg border border-gray-200 p-4">
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <p className="text-xs text-gray-500 mt-0.5 mb-3">{description}</p>
      <div className="flex items-center gap-3">
        <select
          value={mode}
          onChange={e => setMode(e.target.value as RetentionMode)}
          data-testid={`firm-retention-${which}-mode`}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
        >
          <option value="off">Off — never purge</option>
          <option value="days">After N days</option>
        </select>
        {mode === 'days' && (
          <>
            <input
              type="number"
              min={RETENTION_MIN_DAYS}
              max={RETENTION_MAX_DAYS}
              value={days}
              onChange={e => setDays(parseInt(e.target.value, 10) || 0)}
              data-testid={`firm-retention-${which}-days`}
              className="w-28 px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <span className="text-sm text-gray-600">days after completion</span>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="firm-retention-policy">
      <div className="flex items-center gap-2 mb-1">
        <Archive className="w-5 h-5 text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900">Data Retention</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Two independent timers that delete customer data from completed projects. Both start at
        <strong> Off</strong>, and Off means exactly that: <strong>nothing is ever purged automatically</strong> and
        every completed project keeps everything it holds until someone deletes it by hand. Turn a timer on only if
        your firm wants the data gone. Every change is recorded in the firm audit log.
      </p>

      <div className="space-y-3 mb-4">
        {timer(
          'intervals',
          'Delete raw interval data after',
          'Removes the 15-minute meter readings behind a completed project. The calculation results, the pass/fail, and the ratings all survive.',
          intervalsMode, setIntervalsMode, intervalsDays, setIntervalsDays
        )}
        {timer(
          'identity',
          'Remove customer identity after',
          "Clears the end customer's name, address, phone, email, utility account number, notes, and the project title from a completed project. Every calculation figure and the preparer of record survive.",
          identityMode, setIdentityMode, identityDays, setIdentityDays
        )}
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Each timer is either Off or between {RETENTION_MIN_DAYS} and {RETENTION_MAX_DAYS} days. Customer identity
        cannot be removed sooner than the interval data it identifies, so the identity window is never shorter than
        the interval window. Solo accounts have no firm and are never swept.
      </p>

      <div className="flex items-center gap-3">
        <button
          onClick={attemptSave}
          disabled={saving}
          data-testid="firm-retention-save"
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
        >
          {saving ? 'Saving…' : 'Save Retention Policy'}
        </button>
        {msg && <p className="text-sm text-green-700" data-testid="firm-retention-msg">{msg}</p>}
        {error && <p className="text-sm text-red-700" data-testid="firm-retention-error">{error}</p>}
      </div>

      {confirming && (
        <ConfirmDialog
          title={nextIntervals === null && nextIdentity === null
            ? 'Turn automatic deletion off?'
            : 'Turn on automatic deletion?'}
          confirmLabel="Save policy"
          onCancel={() => setConfirming(false)}
          onConfirm={save}
          message={
            <>
              <p>
                {nextIntervals === null && nextIdentity === null
                  ? 'Both timers will be Off. Nothing will be purged automatically, and any project already purged stays purged — this does not bring anything back.'
                  : 'This deletes customer data on a schedule, permanently, with no undo and no export. Read what your firm gives up:'}
              </p>
              {nextIntervals !== null && (
                <p data-testid="retention-confirm-intervals">
                  <strong>Raw interval data, {formatRetentionWindow(nextIntervals)} after completion.</strong> Once a
                  project's interval data is purged, <strong>its report can no longer be regenerated with the load
                  chart.</strong> The numbers stay; the reading-by-reading series that draws the chart is gone.
                </p>
              )}
              {nextIdentity !== null && (
                <p data-testid="retention-confirm-identity">
                  <strong>Customer identity, {formatRetentionWindow(nextIdentity)} after completion.</strong> Once
                  identity is removed, <strong>the project title is replaced with a non-identifying label</strong> and
                  the customer's name, address, phone, email, and account number are cleared. You will no longer be
                  able to tell from TrueLoad whose job it was.
                </p>
              )}
              <p>Projects already past a window are swept on the next nightly run.</p>
            </>
          }
        />
      )}
    </div>
  );
}

// ── Settings Tab ──────────────────────────────────────────────────────────────
function SettingsTab({ firm, onFirmUpdated }: { firm: Firm; onFirmUpdated: (f: Partial<Firm>) => void }) {
  const [firmName, setFirmName] = useState(firm.name);
  const [nameLoading, setNameLoading] = useState(false);
  const [nameMsg, setNameMsg] = useState('');
  const [nameError, setNameError] = useState('');

  type AccessMode = 'personal_only' | 'firm_view' | 'firm_collaborate';

  function deriveAccessMode(visibility: string, editPermission: string): AccessMode {
    if (visibility === 'firm_all' && editPermission === 'firm_all') return 'firm_collaborate';
    if (visibility === 'firm_all') return 'firm_view';
    return 'personal_only';
  }

  const [accessMode, setAccessMode] = useState<AccessMode>(
    deriveAccessMode(firm.project_visibility, firm.project_edit_permission)
  );
  const [projLoading, setProjLoading] = useState(false);
  const [projMsg, setProjMsg] = useState('');
  const [projError, setProjError] = useState('');

  const [newSeatCount, setNewSeatCount] = useState(firm.seat_count);
  const [seatLoading, setSeatLoading] = useState(false);
  const [seatMsg, setSeatMsg] = useState('');
  const [seatError, setSeatError] = useState('');

  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');

  const [logoBusy, setLogoBusy] = useState(false);
  const [logoMsg, setLogoMsg] = useState('');
  const [logoError, setLogoError] = useState('');
  const [confirmRemoveLogo, setConfirmRemoveLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const saveFirmName = async () => {
    setNameLoading(true);
    setNameMsg('');
    setNameError('');
    try {
      const { error } = await supabase
        .from('firms')
        .update({ name: firmName, updated_at: new Date().toISOString() })
        .eq('id', firm.id);
      if (error) throw error;
      onFirmUpdated({ name: firmName });
      setNameMsg('Firm name saved.');
    } catch (err) {
      setNameError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setNameLoading(false);
    }
  };

  const handleLogoChosen = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear the input so choosing the same file again still fires onChange.
    e.target.value = '';
    if (!file) return;
    setLogoBusy(true);
    setLogoMsg('');
    setLogoError('');
    try {
      const url = await uploadFirmLogo(firm.id, file, firm.logo_url);
      onFirmUpdated({ logo_url: url });
      setLogoMsg('Logo saved. It now shows in the header for everyone in your firm and at the top of every report.');
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Failed to upload the logo.');
    } finally {
      setLogoBusy(false);
    }
  };

  const handleRemoveLogo = async () => {
    setConfirmRemoveLogo(false);
    setLogoBusy(true);
    setLogoMsg('');
    setLogoError('');
    try {
      await removeFirmLogo(firm.id, firm.logo_url);
      onFirmUpdated({ logo_url: null });
      setLogoMsg('Logo removed.');
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Failed to remove the logo.');
    } finally {
      setLogoBusy(false);
    }
  };

  const saveProjSettings = async () => {
    setProjLoading(true);
    setProjMsg('');
    setProjError('');
    const visibility = accessMode === 'personal_only' ? 'own_only' : 'firm_all';
    const editPermission = accessMode === 'firm_collaborate' ? 'firm_all' : 'own_only';
    try {
      const { error } = await supabase
        .from('firms')
        .update({ project_visibility: visibility, project_edit_permission: editPermission, updated_at: new Date().toISOString() })
        .eq('id', firm.id);
      if (error) throw error;
      onFirmUpdated({ project_visibility: visibility, project_edit_permission: editPermission });
      setProjMsg('Project settings saved.');
    } catch (err) {
      setProjError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setProjLoading(false);
    }
  };

  const updateSeats = async () => {
    setSeatLoading(true);
    setSeatMsg('');
    setSeatError('');
    try {
      const token = await getToken();
      const res = await fetch('/.netlify/functions/update-firm-seats', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_seat_count: newSeatCount }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update seats.');
      onFirmUpdated({ seat_count: newSeatCount });
      setSeatMsg('Seat count updated. Stripe will prorate the change immediately.');
    } catch (err) {
      setSeatError(err instanceof Error ? err.message : 'Failed to update seats.');
    } finally {
      setSeatLoading(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    setPortalError('');
    try {
      const token = await getToken();
      const res = await fetch('/.netlify/functions/create-portal-session', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || 'Failed to open billing portal.');
      window.location.href = data.url;
    } catch (err) {
      setPortalError(err instanceof Error ? err.message : 'Something went wrong.');
      setPortalLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Firm Details */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Firm Details</h3>
        <label className="block text-sm font-medium text-gray-700 mb-1">Firm Name</label>
        <div className="flex gap-3">
          <input
            type="text"
            value={firmName}
            onChange={e => setFirmName(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={saveFirmName}
            disabled={nameLoading}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            {nameLoading ? 'Saving…' : 'Save'}
          </button>
        </div>
        {nameMsg && <p className="mt-2 text-sm text-green-700">{nameMsg}</p>}
        {nameError && <p className="mt-2 text-sm text-red-700">{nameError}</p>}

        {/* Firm logo — Enterprise only. The file goes to the firm-logos bucket
            and its address to firms.logo_url; the app header and the report
            letterhead read from there. */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <label className="block text-sm font-medium text-gray-700 mb-1">Firm Logo</label>
          <p className="text-xs text-gray-500 mb-3">
            Shown beside the TrueLoad logo for everyone in your firm and printed at the top of every report. PNG, JPG, WebP, or SVG up to 2 MB. A wide, simple mark works best.
          </p>
          <div className="flex items-center gap-4">
            <div
              className="h-16 w-40 flex-shrink-0 flex items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 overflow-hidden"
              data-testid="firm-logo-preview"
            >
              {firm.logo_url ? (
                <img src={firm.logo_url} alt={`${firm.name} logo`} className="max-h-14 max-w-[9.5rem] object-contain" />
              ) : (
                <span className="text-xs text-gray-400">No logo yet</span>
              )}
            </div>
            <div className="flex flex-col items-start gap-2">
              <input
                ref={logoInputRef}
                type="file"
                accept={FIRM_LOGO_ACCEPTED_TYPES.join(',')}
                className="hidden"
                onChange={handleLogoChosen}
                data-testid="firm-logo-input"
              />
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                disabled={logoBusy}
                data-testid="btn-firm-logo-upload"
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
              >
                {logoBusy ? 'Saving…' : firm.logo_url ? 'Replace Logo' : 'Upload Logo'}
              </button>
              {firm.logo_url && (
                <button
                  type="button"
                  onClick={() => setConfirmRemoveLogo(true)}
                  disabled={logoBusy}
                  data-testid="btn-firm-logo-remove"
                  className="text-sm text-red-700 hover:text-red-900 disabled:text-gray-400"
                >
                  Remove logo
                </button>
              )}
            </div>
          </div>
          {logoMsg && <p className="mt-2 text-sm text-green-700">{logoMsg}</p>}
          {logoError && <p className="mt-2 text-sm text-red-700">{logoError}</p>}
        </div>
      </div>

      {confirmRemoveLogo && (
        <ConfirmDialog
          title="Remove firm logo?"
          message="The header and every report generated from now on will show no logo. You can upload a new one at any time."
          onConfirm={handleRemoveLogo}
          onCancel={() => setConfirmRemoveLogo(false)}
        />
      )}

      {/* Project Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Member Project Access</h3>
        <p className="text-sm text-gray-500 mb-4">Controls what firm members can see and edit.</p>
        <div className="space-y-3">
          {([
            {
              value: 'personal_only' as const,
              label: 'Personal Only',
              description: 'Members see and edit only their own projects.',
            },
            {
              value: 'firm_view' as const,
              label: 'Firm View',
              description: 'Members can view and generate reports on all firm projects, but only edit their own.',
            },
            {
              value: 'firm_collaborate' as const,
              label: 'Firm Collaborate',
              description: 'Members can view, edit, and generate reports on all firm projects.',
            },
          ] as const).map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setAccessMode(opt.value)}
              className={`w-full text-left rounded-lg border-2 px-4 py-3 transition-all ${
                accessMode === opt.value
                  ? 'ring-2 ring-blue-600 bg-blue-50 border-blue-600'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>
            </button>
          ))}
        </div>
        <p className="mt-3 text-xs text-gray-400">These settings govern members with seats. As firm admin you see a directory of project names, owners, and status on the Projects tab; project contents are visible to you only if you also hold a seat, under the same settings as any member.</p>
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={saveProjSettings}
            disabled={projLoading}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            {projLoading ? 'Saving…' : 'Save Access Setting'}
          </button>
          {projMsg && <p className="text-sm text-green-700">{projMsg}</p>}
          {projError && <p className="text-sm text-red-700">{projError}</p>}
        </div>
      </div>

      {/* Security (MFA policy + security contact) — changes go through the
          set_firm_mfa_policy RPC so every change lands in the audit log. */}
      <SecurityPolicyCard firm={firm} onFirmUpdated={onFirmUpdated} />

      {/* Data Retention — writes only through set_firm_retention_policy(),
          which audits the change itself. */}
      <RetentionPolicyCard firm={firm} onFirmUpdated={onFirmUpdated} />

      {/* Seat Management */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Seat Management</h3>
        <p className="text-sm text-gray-600 mb-3">Current seat count: <strong>{firm.seat_count} seats</strong></p>
        <label className="block text-sm font-medium text-gray-700 mb-1">New seat count</label>
        <div className="flex gap-3">
          <input
            type="number"
            value={newSeatCount}
            onChange={e => setNewSeatCount(clampSeats(parseInt(e.target.value, 10)))}
            min={SEAT_MIN}
            max={SEAT_MAX}
            className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={updateSeats}
            disabled={seatLoading}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            {seatLoading ? 'Updating…' : 'Update Seats'}
          </button>
        </div>
        {seatMsg && <p className="mt-2 text-sm text-green-700">{seatMsg}</p>}
        {seatError && <p className="mt-2 text-sm text-red-700">{seatError}</p>}
        <p className="mt-2 text-xs text-gray-400">Seat changes are prorated and reflected on your next invoice.</p>
      </div>

      {/* Subscription */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Subscription</h3>
        <div className="mb-4"><StatusBadge status={firm.subscription_status} /></div>
        <button
          onClick={openPortal}
          disabled={portalLoading}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
        >
          {portalLoading ? 'Opening…' : firm.subscription_status !== 'active' ? 'Reactivate Subscription' : 'Manage Subscription'}
        </button>
        {portalError && <p className="mt-2 text-sm text-red-700">{portalError}</p>}
        <p className="mt-2 text-xs text-gray-400">Manage billing, download invoices, or cancel your subscription via the Stripe portal.</p>
      </div>
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────
export default function FirmAdminDashboard({ onClose }: FirmAdminDashboardProps) {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState<FirmTab>('overview');
  const [firm, setFirm] = useState<Firm | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFirmData = async () => {
    if (!userProfile?.firm_id) return;
    setLoading(true);
    setError(null);
    try {
      const [firmRes, membersRes, invitesRes] = await Promise.all([
        supabase.from('firms').select('id, name, logo_url, seat_count, subscription_status, project_visibility, project_edit_permission, mfa_policy, mfa_backup_codes_allowed, mfa_reprompt_days, security_contact_name, security_contact_email, retention_intervals_days, retention_identity_days').eq('id', userProfile.firm_id).single(),
        supabase.from('user_profiles').select('id, full_name, email, firm_admin, has_seat, last_active_at, created_at').eq('firm_id', userProfile.firm_id).order('created_at', { ascending: true }),
        supabase.from('firm_invitations').select('id, invited_email, created_at, expires_at').eq('firm_id', userProfile.firm_id).eq('status', 'pending').order('created_at', { ascending: false }),
      ]);

      if (firmRes.error) throw firmRes.error;
      if (membersRes.error) throw membersRes.error;
      if (invitesRes.error) throw invitesRes.error;

      setFirm(firmRes.data);
      setMembers(membersRes.data || []);
      setPendingInvites(invitesRes.data || []);
    } catch (err) {
      setError('Could not load firm data. Please contact support.');
      console.error('FirmAdminDashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFirmData(); }, [userProfile?.firm_id]);

  const isInactive = firm && firm.subscription_status !== 'active';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to App</span>
          </button>
          <div className="h-5 border-l border-gray-300" />
          <div className="flex items-center gap-2">
            {firm?.logo_url ? (
              <img
                src={firm.logo_url}
                alt=""
                data-testid="firm-dashboard-logo"
                className="h-8 w-auto max-w-[8rem] object-contain"
              />
            ) : (
              <Building2 className="w-5 h-5 text-blue-700" />
            )}
            <h1 className="text-lg font-bold text-gray-900">{firm?.name ?? 'Firm Dashboard'}</h1>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col md:flex-row max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 gap-6">
        {/* Left nav — desktop */}
        <nav className="hidden md:block w-48 flex-shrink-0">
          <ul className="space-y-1">
            {NAV_ITEMS.map(({ id, label, Icon }) => (
              <li key={id}>
                <button
                  onClick={() => setActiveTab(id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === id
                      ? 'bg-blue-100 text-blue-800'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Content */}
        <main className="flex-1 min-w-0">
          {/* Mobile tab bar */}
          <div className="md:hidden mb-4">
            <div className="flex">
              {NAV_ITEMS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex-1 py-2.5 text-sm font-medium text-center transition-colors border-b-2 ${
                    activeTab === id
                      ? 'text-blue-700 border-blue-600'
                      : 'text-gray-500 border-gray-200 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Inactive subscription banner across all tabs */}
          {!loading && isInactive && activeTab !== 'overview' && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">Subscription not active — members cannot access TrueLoad until billing is resolved.</p>
            </div>
          )}

          {loading && (
            <div className="text-center py-12 text-gray-500">Loading firm data…</div>
          )}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-sm text-red-700 mb-3">{error}</p>
              <button onClick={loadFirmData} className="flex items-center gap-2 mx-auto text-sm text-gray-600 hover:text-gray-900">
                <RefreshCw className="w-4 h-4" /> Retry
              </button>
            </div>
          )}
          {!loading && !error && firm && (
            <>
              {activeTab === 'overview' && (
                <OverviewTab firm={firm} members={members} pendingCount={pendingInvites.length} />
              )}
              {activeTab === 'members' && (
                <MembersTab firm={firm} members={members} pendingInvites={pendingInvites} onRefresh={loadFirmData} />
              )}
              {activeTab === 'projects' && (
                <ProjectsTab members={members} />
              )}
              {activeTab === 'activity' && (
                <ActivityTab firm={firm} members={members} />
              )}
              {activeTab === 'settings' && (
                <SettingsTab
                  firm={firm}
                  onFirmUpdated={updates => setFirm(f => f ? { ...f, ...updates } : f)}
                />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
